/**
 * Token Storage Service
 * Centralizes all access token read/write/clear operations.
 * Obfuscates the token in localStorage so it's not visible as plaintext in DevTools.
 *
 * Encoding: reverse + Base64 with a prefix marker ('_at_v2:')
 * This is NOT cryptographic encryption (impossible to truly secure in browser JS),
 * but prevents casual inspection, automated scrapers, and browser extensions
 * from reading the token at a glance.
 */

const STORAGE_KEY = 'anilist_token';
const LEGACY_KEY = 'accessToken';
const PENDING_KEY = 'pending_oauth_token';
const MARKER = '_at_v2:'; // Marker to identify obfuscated tokens

/**
 * Obfuscate a plaintext token for storage
 */
function encode(token) {
    if (!token) return null;
    // Reverse + Base64
    const reversed = token.split('').reverse().join('');
    return MARKER + btoa(reversed);
}

/**
 * De-obfuscate a stored token back to plaintext
 */
function decode(stored) {
    if (!stored) return null;

    // New format: obfuscated with marker
    if (stored.startsWith(MARKER)) {
        try {
            const base64 = stored.slice(MARKER.length);
            const reversed = atob(base64);
            return reversed.split('').reverse().join('');
        } catch {
            return null;
        }
    }

    // Legacy format: plaintext token — return as-is
    return stored;
}

/**
 * Save a token to localStorage (obfuscated)
 */
export function saveToken(token) {
    if (!token) return;
    const encoded = encode(token);
    localStorage.setItem(STORAGE_KEY, encoded);
    // Clean up legacy key if it exists
    localStorage.removeItem(LEGACY_KEY);
    localStorage.removeItem(PENDING_KEY);
}

/**
 * Get the current token (decoded to plaintext for API use)
 */
export function getToken() {
    // Check primary key first
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
        const token = decode(stored);
        // Auto-migrate: if it was a legacy plaintext token, re-save as obfuscated
        if (token && !stored.startsWith(MARKER)) {
            saveToken(token);
        }
        return token;
    }

    // Check legacy key
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) {
        // Migrate to new obfuscated format
        saveToken(legacy);
        return legacy;
    }

    return null;
}

/**
 * Check if a token exists (without decoding)
 */
export function hasToken() {
    return !!(localStorage.getItem(STORAGE_KEY) || localStorage.getItem(LEGACY_KEY));
}

/**
 * Clear the token from localStorage
 */
export function clearToken() {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_KEY);
    localStorage.removeItem(PENDING_KEY);
}

/**
 * Get and consume the pending OAuth token (set during OAuth callback)
 */
export function consumePendingToken() {
    const pending = localStorage.getItem(PENDING_KEY);
    if (pending) {
        localStorage.removeItem(PENDING_KEY);
        return pending; // Pending is always plaintext from the OAuth flow
    }
    return null;
}

/**
 * Save a pending OAuth token (used by OAuthCallback before full login)
 */
export function savePendingToken(token) {
    if (!token) return;
    // Store pending as obfuscated too
    localStorage.setItem(PENDING_KEY, encode(token));
}

export default {
    saveToken,
    getToken,
    hasToken,
    clearToken,
    consumePendingToken,
    savePendingToken
};
